import { createApp } from 'vue';
import type { AppContext } from 'vue';
import ArcoVue, { Modal } from '@arco-design/web-vue';
import type { ModalConfig } from '@arco-design/web-vue/es/modal/interface';
import ArcoVueIcon from '@arco-design/web-vue/es/icon';
import globalComponents from '@/components';
import router from './router';
import store from './store';
import i18n from './locale';
import directive from './directive';
import './mock';
import App from './App.vue';
import { installSingletonErrorMessage } from '@/utils/install-singleton-error-message';
// Styles are imported via arco-plugin. See config/plugin/arcoStyleImport.ts in the directory for details
// 样式通过 arco-plugin 插件导入。详见目录文件 config/plugin/arcoStyleImport.ts
// https://arco.design/docs/designlab/use-theme-package
import '@/assets/style/global.less';
import '@/api/interceptor';

// 全局禁用点击遮罩关闭；保证右上角关闭按钮默认开启
if (Modal.props?.maskClosable) {
  Modal.props.maskClosable.default = false;
}
if (Modal.props?.closable) {
  Modal.props.closable.default = true;
}

// 函数式 Modal（confirm/error 等）强制禁用遮罩关闭，并显示右上角关闭按钮
type ModalMethodKey = 'open' | 'confirm' | 'info' | 'success' | 'warning' | 'error';
const modalMethodKeys: ModalMethodKey[] = [
  'open',
  'confirm',
  'info',
  'success',
  'warning',
  'error',
];
for (const key of modalMethodKeys) {
  const original = Modal[key];
  Modal[key] = ((config: ModalConfig, appContext?: AppContext) =>
    original(
      {
        ...config,
        maskClosable: false,
        closable: true,
        // simple 模式下不渲染右上角关闭按钮，需关闭 simple
        simple: false,
      },
      appContext
    )) as unknown as (typeof Modal)[ModalMethodKey];
}

function shouldReloadForAsyncChunkError(reason: unknown) {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === 'string'
        ? reason
        : '';

  return /Loading chunk \d+ failed|ChunkLoadError|CSS_CHUNK_LOAD_FAILED|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    message
  );
}

function hardReloadOnce() {
  const key = 'app:chunk-load-reload-at';
  const last = Number(sessionStorage.getItem(key) ?? 0);
  const now = Date.now();
  if (now - last < 10_000) return;
  sessionStorage.setItem(key, String(now));

  const url = new URL(window.location.href);
  url.searchParams.set('__reload', String(now));
  window.location.replace(url.toString());
}

window.addEventListener('unhandledrejection', (event) => {
  if (shouldReloadForAsyncChunkError(event.reason)) {
    hardReloadOnce();
  }
});

const app = createApp(App);

app.use(ArcoVue, {});
installSingletonErrorMessage(app);

app.use(ArcoVueIcon);

app.use(router);
app.use(store);
app.use(i18n);
app.use(globalComponents);
app.use(directive);

app.mount('#app');
